import {
  NumberedCitation,
  UnnumberedCitation,
} from '../../modules/wk-document-decorator/models';
import { CommonConstants } from '../../shared/common.constant';

const NO_CITATIONS_ROW = '<tr><td colspan="5">No citations found.</td></tr>';

export interface CitationExportState {
  references: NumberedCitation[];
  unnumbered_references: UnnumberedCitation[];
}

export const downloadCitationLogsDocument = (
  citationState: CitationExportState,
  documentName: string,
  versionLabel?: string
): void => {
  const logContent = buildCitationLogDocumentAsHtml(
    citationState,
    documentName,
    versionLabel
  );
  const blob = new Blob([logContent], {
    type: 'text/html;charset=utf-8',
  });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const fileNamePrefix = (documentName || 'document').replace(/\.[^/.]+$/, '');
  const versionSuffix = versionLabel
    ? `-${sanitizeFileNamePart(versionLabel)}`
    : '';
  const timestamp = new Date().toISOString().replace(/[.:]/g, '-');

  anchor.href = objectUrl;
  anchor.download = `${fileNamePrefix}${versionSuffix}-citation-logs-${timestamp}.html`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
};

const buildCitationLogDocumentAsHtml = (
  citationState: CitationExportState,
  documentName: string,
  versionLabel?: string
): string => {
  const numbered = citationState?.references || [];
  const unnumbered = citationState?.unnumbered_references || [];
  const numberedOrder = getNumberedCitationOrder(numbered);
  const generatedOn = escapeHtml(new Date().toLocaleString());
  const safeDocumentName = escapeHtml(documentName || 'N/A');
  const safeVersionLabel = escapeHtml(versionLabel || 'N/A');
  const numberedRows = buildCitationTableRows(numbered, numberedOrder, false);
  const unnumberedRows = buildCitationTableRows(
    unnumbered,
    numberedOrder,
    true
  );

  return `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Citation Logs</title>
          <style>
            body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #222; margin: 12px; }
            h1 { font-size: 18pt; margin-bottom: 8px; margin-top: 16px; }
            h2 { font-size: 14pt; margin: 24px 0 12px; }
            .meta { margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #b3b3b3; padding: 8px 10px; vertical-align: top; text-align: left; }
            th { background: #e8e8e8; font-weight: bold; }
            td { word-break: break-word; overflow-wrap: break-word; white-space: normal; }
          </style>
        </head>
        <body>
          <h1>Citation Logs</h1>
          <div class="meta"><strong>Generated On:</strong> ${generatedOn}</div>
          <div class="meta"><strong>Document:</strong> ${safeDocumentName}</div>
          <div class="meta"><strong>Version:</strong> ${safeVersionLabel}</div>

          <h2>Numbered Citations</h2>
          <table>
            <thead>
              <tr>
                <th>Citation Number</th>
                <th>Citation ID</th>
                <th>Evidence Level</th>
                <th>Suggested Text</th>
                <th>Citation Text</th>
              </tr>
            </thead>
            <tbody>${numberedRows}</tbody>
          </table>

          <h2>Unnumbered Citations</h2>
          <table>
            <thead>
              <tr>
                <th>Citation Number</th>
                <th>Citation ID</th>
                <th>Evidence Level</th>
                <th>Suggested Text</th>
                <th>Citation Text</th>
              </tr>
            </thead>
            <tbody>${unnumberedRows}</tbody>
          </table>
        </body>
      </html>
    `;
};

const buildCitationTableRows = (
  citations: Array<NumberedCitation | UnnumberedCitation>,
  numberedOrder: Map<string, number>,
  isUnnumbered: boolean
): string => {
  if (!citations.length) {
    return NO_CITATIONS_ROW;
  }

  return citations
    .map((citation: NumberedCitation | UnnumberedCitation, index: number) => {
      const citationNumber = isUnnumbered
        ? `Unnumbered-${index + 1}`
        : `${numberedOrder.get(citation.citationId) || index + 1}`;
      const suggestedText = escapeHtml(
        extractSuggestionText(citation.citationString) || 'N/A'
      );
      const citationText = escapeHtml(
        extractCitationLabelText(citation.citationString) || 'N/A'
      );
      const evidenceLevel = escapeHtml(citation.evidenceLevel || 'No Level');
      const citationId = escapeHtml(citation.citationId || 'N/A');

      return `
          <tr>
            <td>${escapeHtml(citationNumber)}</td>
            <td>${citationId}</td>
            <td>${evidenceLevel}</td>
            <td>${suggestedText}</td>
            <td>${citationText}</td>
          </tr>
        `;
    })
    .join('');
};

const escapeHtml = (value: string): string => {
  return (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const extractSuggestionText = (citationString: string): string => {
  if (!citationString) {
    return '';
  }

  const parser = new DOMParser();
  const parsedHtml = parser.parseFromString(citationString, 'text/html');
  const startNodes = Array.from(
    parsedHtml.body.querySelectorAll(CommonConstants.suggestionStart)
  );

  if (!startNodes.length) {
    return '';
  }

  return startNodes
    .map((startNode: Element) => {
      let currentNode: ChildNode | null = startNode.nextSibling;
      let extractedText = '';

      while (currentNode) {
        if (
          currentNode.nodeType === 1 &&
          (currentNode as Element).tagName.toLowerCase() === 'suggestion-end'
        ) {
          break;
        }

        extractedText += currentNode.textContent || '';
        currentNode = currentNode.nextSibling;
      }

      return normalizeCitationText(extractedText);
    })
    .filter(Boolean)
    .join(' | ');
};

const extractCitationLabelText = (citationString: string): string => {
  if (!citationString) {
    return '';
  }

  const parser = new DOMParser();
  const parsedHtml = parser.parseFromString(citationString, 'text/html');
  const prefLabel = parsedHtml.body.querySelector(
    '[property="skos:prefLabel"]'
  );

  return normalizeCitationText(prefLabel?.textContent || '');
};

const normalizeCitationText = (value: string): string => {
  return (value || '').replace(/\s+/g, ' ').trim();
};

const sanitizeFileNamePart = (value: string): string => {
  return (value || '').replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/-+/g, '-');
};

const getNumberedCitationOrder = (
  citations: NumberedCitation[]
): Map<string, number> => {
  const orderMap = new Map<string, number>();

  citations.forEach((citation: NumberedCitation, index: number) => {
    orderMap.set(citation.citationId, index + 1);
  });

  return orderMap;
};
